import {
  Button, CircularProgress, makeStyles, TextField,
} from '@material-ui/core';
import DoneAll from '@material-ui/icons/DoneAll';
import { useState, useEffect } from 'react';
import clsx from 'clsx';
import tw from 'twin.macro';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useAnalytics } from 'use-analytics';
import { SearchStatus } from '../../constants';
import Checkbox from '../Checkbox';
import ErrorTooltip from '../ErrorTooltip';
import Input from '../Input';
import validateForm from './validateForm';
import getEmailRecipientsFromString from '../../helpers/getEmailRecipientsFromString';
import ChatGPTInterface from '../ChatGPTInterface';
import useBrochureExtractor from '../ChatGPTInterface/extractors/brochureExtractor';
import { useBrochureFunctions } from '../ChatGPTInterface/helpers/brochureUtils';
import PhoneNumberWithCountrySelectInput from '../Input/PhoneNumberWithCountrySelect';
import TipTapEditor from './TipTapEditor';
import useClipboard from '../../hooks/useClipboard';
import generateBrochureLink from '../../helpers/generateBrochureLink';

const textareaStyles = makeStyles(({
  root: {
    '&': tw`border border-gray-500 border-opacity-25 px-6 py-3 rounded-2xl bg-gray-500 bg-opacity-5 placeholder-gray-700 w-full focus:outline-none`,
  },
}));

const textareaInputStyles = makeStyles(({
  input: {
    '&': tw`font-body`,
  },
}));

const sendButtonStyles = makeStyles(({
  root: {
    '&': tw`bg-searchButton-0 rounded-2xl px-5 h-10 text-white font-body w-max m-1`,
    '&:hover': {
      ...tw`bg-searchButton-0`,
      opacity: 0.7,
    },
  },
}));
export const richTextButtonStyles = makeStyles({
  root: {
    '&': tw`bg-gray-100 text-gray-800 border border-gray-300 rounded-2xl px-5 h-10 font-body w-max m-1 transition`,
    '&:hover': tw`bg-gray-200`,
  },
});

const saveButtonStyles = makeStyles(({
  root: {
    '&': tw`bg-searchButton-0 rounded-2xl px-5 h-10 text-white font-body w-max mr-5 m-1`,
    '&:hover': {
      ...tw`bg-searchButton-0`,
      opacity: 0.7,
    },
  },
}));

const copyLinkButtonStyles = makeStyles(({
  root: {
    '&': tw`bg-searchButton-0 rounded-2xl px-5 h-10 text-white font-body w-max mr-5 m-1`,
    '&:hover': {
      ...tw`bg-searchButton-0`,
      opacity: 0.7,
    },
  },
}));

export const showSpinner = (searchStatus, error) => {
  if (searchStatus === SearchStatus.SEARCHING) {
    return <CircularProgress size={25} className="align-text-bottom ml-1" />;
  }
  if (searchStatus === SearchStatus.DONE && !error) {
    return <DoneAll fontSize="small" className="text-green-500 ml-1" />;
  }
  return '';
};

const EmailToClientForm = ({
  createdFor,
  customerName,
  fromEmail,
  emailRecipientsString,
  emailSubject,
  customerEmail,
  customerPhoneNumber,
  doSendCopyToAgent,
  doShowPricing,
  customCheckboxes,
  onInputChange,
  doEnableSave,
  onSavePreviewClick,
  saveStatus,
  saveError,
  saveLabel,
  doEnableSend,
  doEnableCopyLink,
  onSendClick,
  sendStatus,
  sendError,
  sendLabel,
  children,
  brochure,
  emailMessage,
  doHideSentCopyToMe,
  doHideShowPricing,
  doAllowBook,
  doShowBrochureEditor,
}) => {
  const { travelAgency } = useSelector(
    (store) => store.user,
  );
  const doShowChatGPTBrochureMsgInput = travelAgency && travelAgency.settings && travelAgency
    .settings.doShowChatGPTBrochureMsgInput;
  const {
    generalInfo,
    brochureUtilFunctions,
  } = useBrochureFunctions(brochure, doShowChatGPTBrochureMsgInput);

  // TODO: split up email recipients inside this component,
  // so caller doesn't have to
  const [emailFormValidations, setEmailFormValidations] = useState({});
  const [emailRecipients,
    setEmailRecipients] = useState(getEmailRecipientsFromString(emailRecipientsString));
  const [isLinkCopied, copyLinkToClipboard] = useClipboard();
  const textareaClasses = textareaStyles();
  const textareaInputClasses = textareaInputStyles();
  const sendButtonClasses = sendButtonStyles();
  const saveButtonClasses = saveButtonStyles();
  const copyLinkButtonClasses = copyLinkButtonStyles();
  const exportButtonClasses = richTextButtonStyles();
  const { track } = useAnalytics();
  const handleSaveClick = () => {
    const [
      isValid,
      validations,
    ] = validateForm(
      createdFor,
      fromEmail,
      emailRecipients,
      emailSubject,
    );
    setEmailFormValidations(validations);
    if (isValid) {
      onSavePreviewClick();
    }
  };

  const handleInputChange = (event) => {
    setEmailFormValidations({});
    onInputChange(event);
  };

  const handleChangePhoneNumber = (value) => {
    setEmailFormValidations({});
    onInputChange({ target: { name: 'customerPhoneNumber', value: value || '' } });
  };

  const handleSendClick = () => {
    const [
      isValid,
      validations,
    ] = validateForm(
      createdFor,
      fromEmail,
      emailRecipientsString === undefined ? undefined : emailRecipients,
      emailSubject,
      customerEmail,
      customerName,
      customerPhoneNumber,
    );
    setEmailFormValidations(validations);
    if (isValid) {
      onSendClick();
    }
  };

  const handleCopyLinkClick = async () => {
    const brochureId = brochure?.id;
    const urlString = generateBrochureLink(brochureId, travelAgency);
    if (urlString) {
      copyLinkToClipboard(urlString);
      track('brochure.copyLink', { url: urlString });
    }
  };
  const renderCheckboxOptions = () => (
    <>
      {doShowPricing !== undefined && !doHideShowPricing && (
      <div className="mt-4">
        <Checkbox
          checked={doShowPricing || false}
          name="doShowPricing"
          onChange={handleInputChange}
          value={doShowPricing}
          label="Show pricing to customer"
        />
      </div>
      )}
      {doSendCopyToAgent !== undefined && !doHideSentCopyToMe && (
      <div>
        <Checkbox
          checked={doSendCopyToAgent || false}
          name="doSendCopyToAgent"
          onChange={handleInputChange}
          value={doSendCopyToAgent}
          label="Send copy to me"
        />
        {customCheckboxes}
      </div>
      )}
      {doAllowBook !== undefined && (
      <div>
        <Checkbox
          checked={doAllowBook || false}
          name="doAllowBook"
          onChange={handleInputChange}
          value={doAllowBook}
          label="Allow client to self-book from this brochure"
        />
      </div>
      )}
    </>
  );

  useEffect(() => {
    setEmailRecipients(
      getEmailRecipientsFromString(emailRecipientsString),
    );
  }, [emailRecipientsString]);

  return (
    <>
      <div className="grid grid-cols-5 gap-4 my-6">
        <div className="col-span-5 sm:col-span-2">
          {createdFor !== undefined && (
            <div>
              <span className="text-grey-700 ml-6">
                Created for *
              </span>
              <div className="w-full">
                <ErrorTooltip
                  title={(emailFormValidations || {}).createdFor || ''}
                  open={(emailFormValidations || {}).createdFor !== null}
                  arrow
                  PopperProps={{ disablePortal: true }}
                >
                  <div>
                    <Input
                      type="text"
                      name="createdFor"
                      value={createdFor || ''}
                      autoFocus
                      onChange={handleInputChange}
                    />
                  </div>
                </ErrorTooltip>
              </div>
            </div>
          )}
          {customerName !== undefined && (
            <div>
              <span className="text-grey-700 ml-6">
                Your name *
              </span>
              <div className="w-full">
                <ErrorTooltip
                  title={(emailFormValidations || {}).customerName || ''}
                  open={(emailFormValidations || {}).customerName !== null}
                  arrow
                  PopperProps={{ disablePortal: true }}
                >
                  <div>
                    <Input
                      type="text"
                      name="customerName"
                      value={customerName || ''}
                      autoFocus
                      onChange={handleInputChange}
                    />
                  </div>
                </ErrorTooltip>
              </div>
            </div>
          )}
          <div className={clsx({ 'mt-4': createdFor !== undefined || customerName !== undefined })}>
            <span className="text-grey-700 ml-6">
              {customerEmail !== undefined ? 'Your email *' : 'From *'}
            </span>
            <div className="w-full">
              <ErrorTooltip
                title={(emailFormValidations || {})[customerEmail ? 'customerEmail' : 'fromEmail'] || ''}
                open={(emailFormValidations || {})[customerEmail ? 'customerEmail' : 'fromEmail'] !== null}
                arrow
                PopperProps={{ disablePortal: true }}
              >
                <div>
                  <Input
                    type="email"
                    name="fromEmail"
                    required
                    value={customerEmail || fromEmail}
                    onChange={handleInputChange}
                  />
                </div>
              </ErrorTooltip>
            </div>
          </div>
          {emailRecipientsString !== undefined && (
            <div className="mt-4">
              <span className="text-grey-700 ml-6">
                Recipients *
              </span>
              <div className="w-full">
                <ErrorTooltip
                  title={(emailFormValidations || {}).emailRecipients || ''}
                  open={(emailFormValidations || {}).emailRecipients !== ''}
                  arrow
                  PopperProps={{ disablePortal: true }}
                >
                  <div>
                    <Input
                      type="text"
                      name="emailRecipientsString"
                      value={emailRecipientsString || ''}
                      onChange={handleInputChange}
                      placeholder="Separate email addresses by a comma (,)"
                    />
                  </div>
                </ErrorTooltip>
              </div>
            </div>
          )}
          {customerPhoneNumber !== undefined && (
            <div className="mt-4">
              <span className="text-grey-700 ml-6">
                Your phone number
              </span>
              <div className="w-full">
                <ErrorTooltip
                  title={(emailFormValidations || {}).customerPhoneNumber || ''}
                  open={(emailFormValidations || {}).customerPhoneNumber !== null}
                  arrow
                  PopperProps={{ disablePortal: true }}
                >
                  <div>
                    {/* <Input
                      type="number"
                      name="customerPhoneNumber"
                      value={customerPhoneNumber || ''}
                      onChange={handleInputChange}
                    /> */}
                    <PhoneNumberWithCountrySelectInput
                      onChange={handleChangePhoneNumber}
                      value={customerPhoneNumber || ''}
                    />
                  </div>
                </ErrorTooltip>
              </div>
            </div>
          )}
          {emailSubject !== undefined && (
            <div className="mt-4">
              <span className="text-grey-700 ml-6">
                Email Subject *
              </span>
              <div className="w-full">
                <ErrorTooltip
                  title={(emailFormValidations || {}).emailSubject || ''}
                  open={(emailFormValidations || {}).emailSubject !== ''}
                  arrow
                  PopperProps={{ disablePortal: true }}
                >
                  <div>
                    <Input
                      type="text"
                      name="emailSubject"
                      value={emailSubject || ''}
                      onChange={handleInputChange}
                    />
                  </div>
                </ErrorTooltip>
              </div>
            </div>
          )}
          <div className="hidden sm:block">
            {renderCheckboxOptions()}
          </div>
        </div>
        <div className="col-span-5 sm:col-span-3">
          <span className="text-grey-700 ml-6">
            Email Message
          </span>
          <div className="w-full">
            <TextField
              name="emailMessage"
              multiline
              className="w-full h-full"
              rows={10}
              classes={textareaClasses}
              InputProps={{ disableUnderline: true, classes: textareaInputClasses }}
              value={(brochure && brochure.emailMessage) || emailMessage}
              onChange={handleInputChange}
            />
          </div>
          {doShowChatGPTBrochureMsgInput && brochure && (
            <div className="col-span-5 sm:col-span-4 py-6 sm:py-8 block">
              <ChatGPTInterface
                rawData={{ emailMessage: brochure.emailMessage, generalInfo }}
                extractor={useBrochureExtractor}
                utilFunctions={brochureUtilFunctions}
                generalIntro={generalInfo}
                siteType="BROCHURE"
                instructionName="Interface - Brochure Instruction"
              />
            </div>
          )}
          <div className="block sm:hidden mt-2">
            {renderCheckboxOptions()}
          </div>
        </div>
        {brochure && brochure.id
         && doShowBrochureEditor && !!Object.keys(brochure.trips).length && (
         <div className="col-span-5">
           <div className="mt-4">
             <Checkbox
               checked={brochure.doShowBrochureEditor || false}
               name="doShowBrochureEditor"
               onChange={(event) => {
                 handleInputChange(event);
                 track('brochureEditor.toggleEditorVisibility', {
                   brochureId: brochure?.id || 'draft',
                   enabled: event.target.checked,
                 });
               }}
               value={brochure.doShowBrochureEditor}
               label="Use the new experimental editor with modifiable content (free trial)"
             />
           </div>
           {brochure.doShowBrochureEditor
           && (
           <TipTapEditor
             brochure={brochure}
             isVisible={brochure
               .doShowBrochureEditor}
           />
           )}
         </div>
        )}
        <div className="col-span-5">
          {children}
        </div>
        <div className="col-span-5">
          <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-between gap-2">
            <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center w-full sm:w-auto">
              {onSavePreviewClick && (
              <>
                {showSpinner(saveStatus, saveError)}
                <Button
                  classes={saveButtonClasses}
                  disabled={!doEnableSave}
                  onClick={handleSaveClick}
                  disableElevation
                  variant="contained"
                  color="primary"
                >
                  {saveLabel}
                </Button>
              </>
              )}

              {showSpinner(sendStatus, sendError)}
              <Button
                disableElevation
                variant="contained"
                color="primary"
                classes={sendButtonClasses}
                onClick={handleSendClick}
                disabled={!doEnableSend}
              >
                {sendLabel}
              </Button>
              {brochure && (
                <>
                  <div className="w-2 flex justify-center items-center">
                    {isLinkCopied && (
                      <DoneAll fontSize="small" className="text-green-500" />
                    )}
                  </div>
                  <Button
                    disableElevation
                    variant="contained"
                    color="primary"
                    classes={copyLinkButtonClasses}
                    onClick={handleCopyLinkClick}
                    disabled={!doEnableCopyLink || isLinkCopied}
                    style={isLinkCopied ? {
                      backgroundColor: '#43a047',
                      color: '#fff',
                      transition: 'all 0.3s ease',
                    } : {}}
                  >
                    <span className="relative inline-flex items-center justify-center h-full">
                      <span className="opacity-0">Copy link</span>
                      <span className="absolute">
                        {isLinkCopied ? 'Copied' : 'Copy link'}
                      </span>
                    </span>
                  </Button>
                </>
              )}
            </div>
            {brochure?.editorHTML
            && brochure.doShowBrochureEditor && doShowBrochureEditor && (
            <Button
              classes={exportButtonClasses}
              onClick={() => {
                const { editorHTML } = brochure;
                const blob = new Blob([editorHTML], { type: 'text/html' });
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = `brochure_${brochure?.id || 'draft'}.html`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                track('brochureEditor.exportBrochureTemplate', {
                  brochureId: brochure?.id || 'draft',
                  contentLength: editorHTML?.length || 0,
                });
              }}
              disableElevation
              variant="contained"
              color="default"
              disabled={!doEnableSave}
            >
              Export Template
            </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

EmailToClientForm.propTypes = {
  createdFor: PropTypes.string,
  customerName: PropTypes.string,
  fromEmail: PropTypes.string,
  customerEmail: PropTypes.string,
  emailRecipientsString: PropTypes.string,
  emailSubject: PropTypes.string,
  doSendCopyToAgent: PropTypes.bool,
  doShowPricing: PropTypes.bool,
  customerPhoneNumber: PropTypes.string,
  customCheckboxes: PropTypes.node,
  onInputChange: PropTypes.func.isRequired,
  doEnableSave: PropTypes.bool,
  onSavePreviewClick: PropTypes.func,
  saveStatus: PropTypes
    .oneOf(Object.values(SearchStatus)),
  saveError: PropTypes.string,
  saveLabel: PropTypes.string,
  doEnableSend: PropTypes.bool.isRequired,
  doEnableCopyLink: PropTypes.bool.isRequired,
  onSendClick: PropTypes.func.isRequired,
  sendStatus: PropTypes
    .oneOf(Object.values(SearchStatus)),
  sendError: PropTypes.string,
  sendLabel: PropTypes.string.isRequired,
  emailMessage: PropTypes.string,
  children: PropTypes.node,
  brochure: PropTypes.shape({
    emailMessage: PropTypes.string,
    brochureQAs: PropTypes.string,
    trips: PropTypes.objectOf(PropTypes.shape({})),
    id: PropTypes.string,
    doShowBrochureEditor: PropTypes.bool,
    editorHTML: PropTypes.string,
  }),
  doHideSentCopyToMe: PropTypes.bool,
  doHideShowPricing: PropTypes.bool,
  doAllowBook: PropTypes.bool,
  doShowBrochureEditor: PropTypes.bool,
};

EmailToClientForm.defaultProps = {
  createdFor: undefined,
  customerName: undefined,
  fromEmail: undefined,
  customerEmail: undefined,
  emailRecipientsString: undefined,
  emailSubject: undefined,
  doSendCopyToAgent: undefined,
  doShowPricing: undefined,
  customerPhoneNumber: undefined,
  customCheckboxes: undefined,
  doEnableSave: false,
  onSavePreviewClick: undefined,
  saveStatus: undefined,
  saveError: undefined,
  saveLabel: undefined,
  sendStatus: undefined,
  sendError: undefined,
  emailMessage: undefined,
  children: undefined,
  brochure: undefined,
  doHideSentCopyToMe: false,
  doHideShowPricing: false,
  doAllowBook: undefined,
  doShowBrochureEditor: undefined,
};

export default EmailToClientForm;
